// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { TextInput, type TextInputProps } from '@mantine/core';

import { convertDurationToSeconds, convertSecondsToDuration, isDuration } from 'components/Mantine/durationHelpers';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  /** Total duration in seconds. `null` when blank, `NaN` when the typed text isn't a valid duration. */
  value?: number | null;
  /**
   * Called as the user types with the total duration in seconds, `null` when the input is blank,
   * or `NaN` when the typed text isn't a valid duration.
   * @remarks `JSON.stringify(NaN)` evaluates as `null`, so a parent that submits this value without validating it
   * will send invalid input to the server as a blank duration. The parent's validation must reject `NaN`, either
   * directly (`Number.isNaN`) or through a check that already covers it, like a falsy check on a required field.
   */
  onChange: (seconds: number | null) => void;
  /** Called on an Enter key press */
  onSubmit?: () => void;
  /**
   * Maximum allowed value in seconds
   * @defaultValue 3_599_940 (999:59)
   */
  max?: number;
} & StrongOmit<TextInputProps, 'value' | 'defaultValue' | 'onChange' | 'onSubmit' | 'max'>;

const durationAsNumberRegex = /^\d*(\.\d+)?$/;

/** `null` for blank text, `NaN` for text that isn't a duration of at most `max` seconds. */
const parseDuration = (text: string, max: number): number | null => {
  if (text === '') return null;
  const seconds = isDuration(text)
    ? convertDurationToSeconds(text)
    : // Duration is in hours, ie. 2.5
      durationAsNumberRegex.test(text)
      ? Math.floor(parseFloat(text) * 3_600)
      : NaN;
  return seconds <= max ? seconds : NaN;
};

const formatSeconds = (seconds: number | null) =>
  isNotNil(seconds) && seconds > 0 ? convertSecondsToDuration(seconds) : '';

/**
 * Used to collect a number of hours and minutes from a user, as a total number of seconds.
 *
 * Accepts `hh:mm` or total hours (ie. `2.5`). A blank input is `null`.
 * Text that isn't a valid duration is reported to `onChange` as `NaN`. The parent is responsible for
 * rejecting it and passing back an `error` to show.
 */
export const DurationInput = ({
  value: seconds = null,
  onChange,
  onSubmit,
  max = 3_599_940,
  error,
  ...props
}: Props) => {
  // The typed text is kept separately from `seconds` because several texts map to the same
  // number of seconds (`2.5`, `02:30`), and partially typed text maps to nothing.
  const [text, setText] = useState(() => formatSeconds(seconds));

  useEffect(() => {
    // Only overwrite the text when it no longer represents `seconds`,
    // so the user's in-progress formatting isn't clobbered while typing.
    // `Object.is` so invalid text is kept while `seconds` is `NaN`.
    setText((t) => (Object.is(parseDuration(t, max), seconds) ? t : formatSeconds(seconds)));
  }, [seconds, max]);

  const parsed = parseDuration(text, max);
  const isParsable = !Number.isNaN(parsed);

  return (
    <TextInput
      w={120}
      styles={{
        input: {
          textAlign: 'right',
          fontSize: 16,
        },
      }}
      leftSection={
        <InfoIcon
          color={error ? 'red' : undefined}
          label={
            <>
              Allowed formats:
              <ul style={{ margin: 0, textAlign: 'left' }}>
                <li>
                  <code>hh:mm</code> (
                  <b>
                    <code>02:30</code>
                  </b>
                  )
                </li>
                <li>
                  total hours (eg{' '}
                  <b>
                    <code>2.5</code>
                  </b>
                  )
                </li>
              </ul>
            </>
          }
        />
      }
      maxLength={10}
      autoFocus
      onFocus={(e) => e.currentTarget.setSelectionRange(0, e.currentTarget.value.indexOf(':'))}
      placeholder="hh:mm"
      {...props}
      error={error}
      value={text}
      onChange={({ currentTarget: { value: newText } }) => {
        setText(newText);
        onChange(parseDuration(newText, max));
      }}
      onBlur={(e) => {
        // Normalize valid text to `hh:mm`. Invalid text stays as typed so the user can see what to fix.
        if (isParsable) setText(formatSeconds(parsed));
        props.onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSubmit?.();
        if (e.key === 'Tab') {
          const v = e.currentTarget.value;
          const colonPosition = v.indexOf(':');
          const cursorPosition = e.currentTarget.selectionStart ?? colonPosition;
          if (colonPosition > 0) {
            if (colonPosition >= cursorPosition) {
              e.currentTarget.setSelectionRange(colonPosition + 1, v.length);
            } else {
              e.currentTarget.setSelectionRange(0, colonPosition);
            }
          }
          e.preventDefault();
        }
        props.onKeyDown?.(e);
      }}
    />
  );
};
